import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

// ================================
// INTERFACES
// ================================

export interface WidgetConfig {
  id: string;
  type:
    | 'metric_card'
    | 'line_chart'
    | 'bar_chart'
    | 'pie_chart'
    | 'table'
    | 'heatmap'
    | 'gauge'
    | 'trend'
    | 'comparison'
    | 'list';
  title: string;
  subtitle?: string;
  dataSource: string;
  metric?: string;
  aggregation?: 'sum' | 'avg' | 'count' | 'min' | 'max';
  groupBy?: string;
  filters?: {
    branchId?: string;
    dateRange?: {
      type: 'relative' | 'absolute';
      value: string | number;
      startDate?: string;
      endDate?: string;
    };
    customFilters?: Record<string, unknown>;
  };
  display: {
    width: 1 | 2 | 3 | 4;
    height: 1 | 2 | 3;
    position: { row: number; col: number };
    colorScheme?: string;
    showTrend?: boolean;
    showComparison?: boolean;
    comparisonPeriod?: 'previous_period' | 'same_period_last_year';
    valueFormat?: 'number' | 'currency' | 'percentage';
    decimals?: number;
  };
  refreshInterval?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface Dashboard {
  _id: string;
  name: string;
  description?: string;
  slug: string;
  type: 'personal' | 'team' | 'system';
  category: 'overview' | 'sales' | 'inventory' | 'operations' | 'loyalty' | 'custom';
  createdBy: {
    _id: string;
    name: string;
  };
  ownerId?: string;
  branchId?: string;
  isPublic: boolean;
  sharedWith: Array<{
    userId?: string;
    role?: string;
    permission: 'view' | 'edit';
  }>;
  widgets: WidgetConfig[];
  layout: {
    columns: number;
    rowHeight: number;
    gap: number;
  };
  defaultFilters?: {
    dateRange?: {
      type: 'relative' | 'absolute';
      value: string;
    };
    branchId?: string;
  };
  theme?: 'light' | 'dark' | 'system';
  accentColor?: string;
  isActive: boolean;
  isPinned: boolean;
  sortOrder: number;
  viewCount: number;
  lastViewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ParsedNLQQuery {
  intent: 'show' | 'compare' | 'list' | 'count' | 'trend' | 'top' | 'bottom' | 'change_visualization';
  dataSource: string;
  metrics: string[];
  filters: Record<string, unknown>;
  groupBy?: string;
  timeRange?: {
    type: string;
    value: unknown;
  };
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  visualization?: string;
  confidence?: number;
  clarificationNeeded?: string;
}

export interface NLQQueryResult {
  query: string;
  parsed: ParsedNLQQuery;
  result: unknown;
  answer?: string;
  insights?: string[];
  metadata: {
    executionTimeMs: number;
    resultCount: number;
  };
  sessionId?: string;
  historyLength?: number;
}

export interface NLQQueryHistory {
  _id: string;
  query: string;
  parsedQuery: {
    intent: string;
    dataSource: string;
  };
  wasSuccessful: boolean;
  createdAt: string;
}

export interface NLQSuggestion {
  category: string;
  examples: string[];
}

export interface SavedReport {
  _id: string;
  name: string;
  description?: string;
  type: 'sales' | 'inventory' | 'operations' | 'loyalty' | 'waste' | 'custom';
  dataSource: string;
  filters: Record<string, unknown>;
  groupBy?: string;
  isScheduled: boolean;
  lastGeneratedAt?: string;
  generationCount: number;
  createdAt: string;
  updatedAt: string;
}

// ================================
// DASHBOARD QUERIES
// ================================

/**
 * Get list of dashboards
 */
export function useDashboards(options?: { type?: string; category?: string }) {
  return useQuery({
    queryKey: ['dashboard', 'list', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.type) params.append('type', options.type);
      if (options?.category) params.append('category', options.category);

      const response = await apiClient.get<APIResponse<{ dashboards: Dashboard[] }>>(
        `/dashboard/list?${params.toString()}`
      );
      return response.data.data.dashboards;
    },
  });
}

/**
 * Get dashboard by slug
 */
export function useDashboard(slug?: string) {
  return useQuery({
    queryKey: ['dashboard', slug],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ dashboard: Dashboard }>>(
        `/dashboard/${slug}`
      );
      return response.data.data.dashboard;
    },
    enabled: !!slug,
  });
}

// ================================
// WIDGET DATA QUERIES
// ================================

interface WidgetDataParams {
  dataSource: string;
  metric?: string;
  aggregation?: string;
  groupBy?: string;
  filters?: Record<string, unknown>;
}

/**
 * Fetch widget data
 */
export function useWidgetData(params: WidgetDataParams, enabled = true) {
  return useQuery({
    queryKey: ['dashboard', 'widget', params],
    queryFn: async () => {
      const response = await apiClient.post<
        APIResponse<{
          dataSource: string;
          result: unknown;
          filters: { startDate: string; endDate: string; branchId?: string };
        }>
      >('/dashboard/widget/data', params);
      return response.data.data;
    },
    enabled,
    staleTime: 60 * 1000, // 1 minute
  });
}

// ================================
// NLQ QUERIES
// ================================

/**
 * Get NLQ query suggestions
 */
export function useNLQSuggestions() {
  return useQuery({
    queryKey: ['dashboard', 'nlq', 'suggestions'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ suggestions: NLQSuggestion[] }>>(
        '/dashboard/nlq/suggestions'
      );
      return response.data.data.suggestions;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Get NLQ query history
 */
export function useNLQHistory(limit = 20) {
  return useQuery({
    queryKey: ['dashboard', 'nlq', 'history', limit],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ history: NLQQueryHistory[] }>>(
        `/dashboard/nlq/history?limit=${limit}`
      );
      return response.data.data.history;
    },
  });
}

// ================================
// SAVED REPORTS QUERIES
// ================================

/**
 * Get saved reports
 */
export function useSavedReports(type?: string) {
  return useQuery({
    queryKey: ['dashboard', 'reports', type],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (type) params.append('type', type);

      const response = await apiClient.get<APIResponse<{ reports: SavedReport[] }>>(
        `/dashboard/reports?${params.toString()}`
      );
      return response.data.data.reports;
    },
  });
}
